import React from 'react';
import { Container, Row, Col, Button, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
  FaGithub,
  FaLinkedin,
  FaTwitter,
  FaCode,
  FaPen,
  FaDatabase,
  FaVideo,
  FaMapMarkerAlt,
  FaLaptopCode,
  FaRocket,
  FaCoffee,
  FaHeart,
} from 'react-icons/fa';

const Hero = ({ currentTheme }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  const floatingAnimation = {
    y: [-10, 10],
    transition: {
      duration: 2,
      repeat: Infinity,
      repeatType: 'reverse',
      ease: 'easeInOut',
    },
  };

  return (
    <div
      className="hero-section"
      style={{ 
        background: currentTheme.background, 
        color: currentTheme.text 
      }}
    >
      <Container fluid className="min-vh-100 d-flex align-items-center position-relative py-5" id="hero">
        <Container>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Row className="align-items-center gy-5">
              <Col lg={6}>
                <motion.div variants={itemVariants}>
                  <Badge
                    bg="primary"
                    className="mb-3 p-2 fs-6"
                    style={{ backgroundColor: currentTheme.primary }}
                  >
                    <FaMapMarkerAlt className="me-2" />
                    Based in Nairobi, Kenya 🌍
                  </Badge>

                  <h1 className="display-3 fw-bold mb-3 lh-1">
                    Hello<span role="img" aria-label="wave"> 👋</span><span style={{ color: currentTheme.text }}>.</span> 
                  </h1>
                  <h2 className="fw-bold mb-3 lh-1">
                    I'm <span style={{ color: currentTheme.primary }}>Sylvester Omondi</span><span style={{ color: currentTheme.text }}>.</span>
                  </h2>

                  <p className="lead mb-4" style={{ color: currentTheme.text }}>
                    A passionate tech enthusiast 🚀, blending creativity and logic as a Software Developer, 
                    Technical Writer ✍️, CCTV and IT Systems Specialist. Always learning 🔥.
                  </p>

                  <div className="d-flex flex-wrap gap-4 mb-4">
                    {[
                      { icon: FaCode, text: 'Frontend Developer', color: 'primary' },
                      { icon: FaCode, text: 'Backend Developer', color: 'primary' },
                      { icon: FaPen, text: 'Technical Writer', color: 'success' },
                      { icon: FaPen, text: 'Research Writer', color: 'success' },
                      { icon: FaVideo, text: 'CCTV Specialist', color: 'warning' },
                      { icon: FaLaptopCode, text: 'IT Administrator', color: 'info' },
                    ].map((item, index) => (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.05 }}
                        className={`d-flex align-items-center text-${item.color}`}
                      >
                        <item.icon className="fs-4 me-2" />
                        <span className="fw-bold">{item.text}</span>
                      </motion.div>
                    ))}
                  </div>

                  <div className="d-flex flex-wrap gap-3 mb-5">
                    <motion.div whileHover={{ scale: 1.05 }}>
                      <Button
                        variant="primary"
                        size="lg"
                        className="rounded-pill px-4"
                        href="https://drive.google.com/file/d/1jRHSbwz6ZOClJFy4kaqGy638AWI1w5-8/view?usp=drive_link"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ backgroundColor: currentTheme.primary, borderColor: currentTheme.primary }}
                      >
                        <FaRocket className="me-2" />
                        View Resume
                      </Button>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.05 }}>
                      <Button
                        variant="outline-primary"
                        size="lg"
                        className="rounded-pill px-4"
                        href="https://www.linkedin.com/in/sylvester-omondi-2868b8219/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: currentTheme.primary, borderColor: currentTheme.primary }}
                      >
                        <FaCoffee className="me-2" />
                        Let's Connect
                      </Button>
                    </motion.div>
                  </div>

                  <div className="d-flex gap-4 align-items-center">
                    {[
                      { icon: FaGithub, link: 'https://github.com/OMONDISOFTWAREENGINEER' },
                      { icon: FaLinkedin, link: 'https://www.linkedin.com/in/sylvester-omondi-2868b8219/' },
                      { icon: FaTwitter, link: 'https://x.com/sylvest34071957' },
                    ].map((social, index) => (
                      <motion.a
                        key={index}
                        href={social.link}
                        className="fs-3"
                        style={{ color: currentTheme.text }}
                        whileHover={{ scale: 1.2, rotate: 5 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <social.icon />
                      </motion.a>
                    ))}
                  </div>
                </motion.div>
              </Col>

              <Col lg={6}>
                <motion.div
                  className="position-relative"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <div className="position-relative">
                    <motion.div animate={floatingAnimation} className="text-center">
                      <div
                        className="hero-image-container rounded-circle shadow-lg mx-auto overflow-hidden"
                        style={{
                          width: '380px',
                          height: '380px',
                          background: currentTheme.background === '#121212' ? '#2a2a2a' : '#f8f9fa',
                          border: `5px solid ${currentTheme.background === '#121212' ? '#343a40' : '#fff'}`,
                          position: 'relative',
                        }}
                      >
                        <img
                          src="https://i.ibb.co/VtSYGD9/A-3.jpg"
                          alt="Sylvester Omondi"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center top',
                            position: 'absolute',
                            top: '0',
                            left: '0',
                          }}
                        />
                      </div>
                    </motion.div>

                    {/* Floating Elements */}
                    {[
                      { icon: FaLaptopCode, color: 'success', position: { top: '10%', right: '10%' } },
                      { icon: FaHeart, color: 'danger', position: { bottom: '15%', right: '15%' } },
                      { icon: FaDatabase, color: 'info', position: { bottom: '10%', left: '10%' } },
                    ].map((item, index) => (
                      <motion.div
                        key={index}
                        className="position-absolute"
                        style={item.position}
                        animate={{
                          rotate: 360,
                          y: [0, -10, 0],
                        }}
                        transition={{
                          duration: 3 + index,
                          repeat: Infinity,
                          ease: 'linear',
                        }}
                      >
                        <div
                          className={`bg-${item.color} rounded-circle p-3 shadow`}
                        >
                          <item.icon className="fs-4 text-white" />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </Container>

      {/* Custom CSS */}
      <style jsx>{`
        .hero-section {
          position: relative;
          overflow: hidden;
          font-family: 'Poppins', sans-serif;
        }

        @media (max-width: 768px) {
          .hero-image-container {
            width: 280px !important;
            height: 280px !important;
          }
        }

        .hero-image-container img {
          transition: transform 0.3s ease-in-out;
        }

        .hero-image-container:hover img {
          transform: scale(1.05);
        }
      `}</style>
    </div>
  );
};

export default Hero;